import {Box, Popover, Stack, Typography} from '@mui/material';
import {InputRule, mergeAttributes, Node} from '@tiptap/core';
import {NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps} from '@tiptap/react';
import {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AutomationVariableContext} from './AutomationVariableNode';
import {inlineVariableChipSx, variableChipSx} from '../../../../../common/variables/variableChipStyles';
import {automationVariableLabel} from '../../../config/variableReferences';
import {readAutomationVariableReference} from './automationVariableText';

function AutomationPlaceholderNodeView({node, editor, selected, getPos}: NodeViewProps) {
    const {t} = useTranslation();
    const {placeholders, disabled} = useContext(AutomationVariableContext);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const key = node.attrs.key as string;
    const mapping = placeholders.find(placeholder => placeholder.key === key);
    const source = mapping ? readAutomationVariableReference(mapping.value) : null;
    const outlineColor = mapping?.value ? '#7C3AED' : 'error.main';

    return <NodeViewWrapper as={'span'} style={{whiteSpace: 'nowrap'}}>
        <Box component={'button'} type={'button'} contentEditable={false} disabled={disabled}
             data-automation-placeholder={key} aria-label={`${t('Variable')}: ${key}`} title={key}
             onMouseDown={event => event.preventDefault()}
             onClick={event => {
                 const position = getPos();
                 if (typeof position === 'number') editor.commands.setNodeSelection(position);
                 setAnchor(event.currentTarget);
             }}
             sx={{
                 ...inlineVariableChipSx, border: 0, fontSize: 'inherit', fontFamily: 'inherit', lineHeight: 'inherit',
                 px: '0.35em', py: '0.12em', mx: '2px', verticalAlign: 'baseline',
                 cursor: disabled ? 'default' : 'pointer',
                 outline: '2px solid', outlineColor: selected || anchor ? outlineColor : 'transparent', outlineOffset: '1px',
                 '&:hover, &:focus-visible': {outlineColor},
             }}>{key}</Box>
        <Popover open={!disabled && !!anchor} anchorEl={anchor} disableRestoreFocus transitionDuration={0}
                 onClose={() => { setAnchor(null); editor.commands.focus(); }}
                 anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                 slotProps={{paper: {sx: {mt: 1, maxWidth: 380, borderRadius: 2}}}}>
            <Stack gap={1} sx={{p: 2}}>
                <Box><Box component={'span'} sx={variableChipSx}>{key}</Box></Box>
                <Typography variant={'body2'} color={'text.secondary'}>{t('Step value')}</Typography>
                <Typography variant={'body2'} sx={{overflowWrap: 'anywhere'}}>
                    {source ? automationVariableLabel(source) : mapping?.value || t('Not configured')}
                </Typography>
            </Stack>
        </Popover>
    </NodeViewWrapper>;
}

/** A mapped placeholder retains its [[key]] text representation, including when typed or pasted. */
const AutomationPlaceholderNode = Node.create({
    name: 'automationPlaceholder', group: 'inline', inline: true, atom: true, selectable: true,
    addAttributes() { return {key: {default: ''}}; },
    renderHTML({node, HTMLAttributes}) { return ['span', mergeAttributes(HTMLAttributes), `[[${node.attrs.key}]]`]; },
    renderText({node}) { return `[[${node.attrs.key}]]`; },
    addInputRules() {
        return [new InputRule({
            find: /\[\[([^\[\]\r\n]+)\]\]$/,
            handler: ({state, range, match}) => {
                state.tr.replaceWith(range.from, range.to, this.type.create({key: match[1]}));
            },
        })];
    },
    addNodeView() { return ReactNodeViewRenderer(AutomationPlaceholderNodeView); },
});

export default AutomationPlaceholderNode;
